import { createTheme, rem } from '@mantine/core'
import type { CSSVariablesResolver } from '@mantine/core'
import { ActionIconThemeKobo } from './ActionIcon'
import { AlertThemeKobo } from './Alert'
import { AutocompleteThemeKobo } from './Autocomplete'
import { ButtonThemeKobo } from './Button'
import { CheckboxThemeKobo } from './Checkbox'
import { CloseButtonThemeKobo } from './CloseButton'
import { CodeThemeKobo } from './Code'
import { DividerThemeKobo } from './Divider'
import { DropzoneThemeKobo } from './Dropzone'
import { InputThemeKobo } from './Input'
import { InputBaseThemeKobo } from './InputBase'
import { LoaderThemeKobo } from './Loader'
import { MenuThemeKobo } from './Menu'
import { ModalThemeKobo } from './Modal'
import { MultiSelectThemeKobo } from './MultiSelect'
import { NotificationThemeKobo } from './Notification'
import { NumberInputThemeKobo } from './NumberInput'
import { PaperThemeKobo } from './Paper'
import { PasswordInputThemeKobo } from './PasswordInput'
import { PillThemeKobo } from './Pill'
import { RadioThemeKobo } from './Radio'
import { ScrollAreaThemeKobo } from './ScrollArea'
import { SelectThemeKobo } from './Select'
import { SwitchThemeKobo } from './Switch'
import { TableThemeKobo } from './Table'
import { TabsThemeKobo } from './Tabs'
import { TagsInputThemeKobo } from './TagsInput'
import { TextareaThemeKobo } from './Textarea'
import { ThemeIconThemeKobo } from './ThemeIcon'
import { TooltipThemeKobo } from './Tooltip'
import { KOBO_Z_INDEX, KOBO_Z_INDEX_CSS_VARS } from './zIndex'

export const cssVariablesResolverKobo: CSSVariablesResolver = () => {
  return {
    variables: {
      '--kobo-z-index-modal-overlay': String(KOBO_Z_INDEX.modalOverlay),
      '--kobo-z-index-modal': String(KOBO_Z_INDEX.modal),
      '--kobo-z-index-tooltip': String(KOBO_Z_INDEX.tooltip),
      '--kobo-z-index-nested-modal-overlay': String(KOBO_Z_INDEX.nestedModalOverlay),
      '--kobo-z-index-nested-modal': String(KOBO_Z_INDEX.nestedModal),
      '--kobo-z-index-dropdown': String(KOBO_Z_INDEX.dropdown),
      '--kobo-focus-ring-color': 'var(--mantine-primary-color-filled)',
      '--kobo-focus-ring-color-danger': 'var(--mantine-color-red-6)',
      '--kobo-focus-ring-width': '2px',
      '--kobo-focus-ring-offset': '2px',
      '--kobo-focus-ring': 'var(--kobo-focus-ring-width) solid var(--kobo-focus-ring-color)',
      '--kobo-choice-control-disabled-bg': 'var(--mantine-color-gray-8)',
      '--kobo-choice-control-disabled-border': 'var(--mantine-color-gray-5)',
      '--kobo-choice-control-disabled-icon': 'var(--mantine-color-gray-4)',
      '--kobo-choice-control-disabled-label': 'var(--mantine-color-gray-3)',
    },
    light: {},
    dark: {},
  }
}

export const themeKobo = createTheme({
  focusRing: 'auto',
  primaryColor: 'blue',
  colors: {
    gray: [
      '#050f1c', // [0] IITA
      '#07162a', // [1] IITA
      '#4a5262', // [2] IITA
      '#7a808c', // [3] IITA
      '#a3a9b5', // [4] IITA
      '#c8ccd5', // [5] IITA
      '#e3e6ed', // [6] IITA
      '#eef0f4', // [7] IITA
      '#f5f6f9', // [8] IITA
      '#ffffff', // [9] IITA
    ],
    blue: [
      '#000', // [0] IITA
      '#000', // [1] IITA
      '#000', // [2] IITA
      '#8f3a0f', // [3] IITA
      '#c94f14', // [4] IITA
      '#d95a1a', // [5] IITA
      '#f26522', // [6] IITA
      '#f9b48f', // [7] IITA
      '#fde0d0', // [8] IITA
      '#fef1ea', // [9] IITA
    ],
    teal: [
      '#000', // [0] IITA
      '#000', // [1] IITA
      '#146536', // [2] IITA
      '#1a7741', // [3] IITA
      '#1f8a4c', // [4] IITA
      '#8fcfa8', // [5] IITA
      '#c7e8d3', // [6] IITA
      '#e5f4ea', // [7] IITA
      '#000', // [8] IITA
      '#000', // [9] IITA
    ],
    red: [
      '#000', // [0]
      '#000', // [1]
      '#000', // [2]
      '#000', // [3]
      'hsl(0, 68%, 22%)', // [4] #601212;
      'hsl(0, 100%, 26%)', // [5] #850000
      'hsl(0, 100%, 31%)', // [6] #9d0000
      'hsl(0, 100%, 75%)', // [7] #ff8080
      'hsl(0, 100%, 90%)', // [8] #ffcccc
      'hsl(0, 100%, 96%)', // [9] #ffe9e9
    ],
    amber: [
      'hsl(28, 100%, 15%)', // [0]
      'hsl(29, 100%, 22%)', // [1] approx $kobo-dark-amber
      'hsl(29, 100%, 29%)', // [2]
      'hsl(29, 100%, 36%)', // [3]
      'hsl(29, 100%, 43%)', // [4]
      'hsl(36, 100%, 60%)', // [5]
      'hsl(36, 100%, 70%)', // [6] approx $kobo-amber
      'hsl(36, 100%, 80%)', // [7]
      'hsl(36, 100%, 90%)', // [8] $kobo-light-amber
      'hsl(41, 100%, 95%)', // [9]
    ],
  },

  // Typography
  scale: 16 / 14, // Because old ways set base font to 14px instead of standard 16px.
  fontFamily: '"Public Sans", "Segoe UI", Arial, sans-serif',
  fontFamilyMonospace: 'Roboto Mono, monospace',
  fontSizes: {
    xs: rem(12),
    sm: rem(13), // TODO: For now implied from button sizes.
    md: rem(14), // TODO: For now implied from button sizes.
    lg: rem(16),
    xl: rem(18),
  },
  // Kobo uses 20+ different line-heights in different units. TODO: standardize and use mantine config.
  lineHeights: {},
  headings: {
    fontWeight: '500',
  },
  // headings: {
  //   fontFamily: '"Roboto", sans-serif',
  // },

  defaultRadius: 'md',
  radius: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '10px',
    xl: '14px',
  },

  spacing: {
    xxs: '8px',
  },

  other: {
    zIndex: KOBO_Z_INDEX,
    zIndexCssVars: KOBO_Z_INDEX_CSS_VARS,
  },

  // Note: please keep this list in abc order <3
  components: {
    ActionIcon: ActionIconThemeKobo,
    Alert: AlertThemeKobo,
    Autocomplete: AutocompleteThemeKobo,
    Button: ButtonThemeKobo,
    Checkbox: CheckboxThemeKobo,
    CloseButton: CloseButtonThemeKobo,
    Code: CodeThemeKobo,
    Divider: DividerThemeKobo,
    Dropzone: DropzoneThemeKobo,
    Input: InputThemeKobo,
    InputBase: InputBaseThemeKobo,
    Loader: LoaderThemeKobo,
    Menu: MenuThemeKobo,
    Modal: ModalThemeKobo,
    MultiSelect: MultiSelectThemeKobo,
    Notification: NotificationThemeKobo,
    NumberInput: NumberInputThemeKobo,
    Paper: PaperThemeKobo,
    PasswordInput: PasswordInputThemeKobo,
    Pill: PillThemeKobo,
    Radio: RadioThemeKobo,
    ScrollArea: ScrollAreaThemeKobo,
    Select: SelectThemeKobo,
    Switch: SwitchThemeKobo,
    Table: TableThemeKobo,
    Tabs: TabsThemeKobo,
    TagsInput: TagsInputThemeKobo,
    Textarea: TextareaThemeKobo,
    ThemeIcon: ThemeIconThemeKobo,
    Tooltip: TooltipThemeKobo,
  },
})
